import { useState } from 'react';
import { SortableContext, verticalListSortingStrategy, SortingStrategy } from '@dnd-kit/sortable';
import { useDroppable, useDndMonitor } from '@dnd-kit/core';
import { ECategoryType, EDropCategory } from '../../../data/Constants';
import { SortableItem } from './SortableItem';

import type { DroppableZoneProps } from '../../../../types';
/**
 * Custom strategy that maintains the original order (prevents actual sorting)
 * This strategy is used when items should be draggable but not sortable
 *
 * @param {Object} args - The sorting strategy arguments
 * @returns {Object} Coordinates that match the original order so items don't get rearranged
 */
const staticOrderStrategy: SortingStrategy = (args) => {
  // Return coordinates that match the original order so items don't get rearranged
  return {
    x: 0,
    y: args.index, // Keep the vertical order as is
    scaleX: 1,
    scaleY: 1,
  };
};

/**
 * Props for the DroppableZone component
 * @interface DroppableZoneProps
 */

/**
 * A component that creates a droppable zone for drag and drop functionality
 *
 * @param {DroppableZoneProps} props - The component props
 * @returns {React.ReactElement} The rendered DroppableZone component
 */
export const DroppableZone: React.FC<DroppableZoneProps> = ({
  id,
  items,
  isSortable = false,
  onRemove,
  itemType,
  isValidDragItem = true,
  overId,
  containerType,
  isEditMode,
}) => {
  // Add state to track when any item is being dragged
  const [isDragging, setIsDragging] = useState(false);

  /**
   * Hook that registers the dropppable area and provides state information
   */
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: {
      type: id === EDropCategory.Assigned ? EDropCategory.Assigned : EDropCategory.Unassigned,
      container: id,
      itemType: itemType,
    },
  });

  /**
   * Hook that monitors drag operations globally and updates local state
   */
  useDndMonitor({
    /**
     * Handler called when drag operation starts
     */
    onDragStart: () => {
      setIsDragging(true);
    },
    /**
     * Handler called when drag operation ends
     */
    onDragEnd: () => {
      setIsDragging(false);
    },
    /**
     * Handler called when drag operation is cancelled
     */
    onDragCancel: () => {
      setIsDragging(false);
    },
  });

  /**
   * Determines the CSS classes for the drop area based on current drag state
   *
   * @returns {string} Space-separated list of CSS class names
   */
  const getDropAreaClasses = () => {
    const baseClasses = ['drop-area'];

    // Add drag state classes
    if (itemType === ECategoryType.MultiNumeric && !isValidDragItem) {
      baseClasses.push('invalid');
    } else if (isOver || overId) {
      // Show highlighting when either isOver is true OR when this zone is the target (overId)
      baseClasses.push('over');
    }

    // Add dragging active class when an item is being dragged and this zone is the target
    if (isDragging && overId) {
      baseClasses.push('dragging-active');
    }

    return baseClasses.filter(Boolean).join(' ');
  };

  const dropAreaClasses = getDropAreaClasses();

  // All items should be draggable, but only items in sortable zones should be sortable
  return (
    <div className="droppable-zone">
      <div ref={setNodeRef} data-droppable={id} className={dropAreaClasses}>
        {' '}
        <div className="items-container">
          <SortableContext
            items={items.map((i) => i.code)}
            strategy={isSortable ? verticalListSortingStrategy : staticOrderStrategy}
          >
            {items?.map((item) => (
              <SortableItem
                key={item.code}
                id={item.code}
                item={item}
                onRemove={isSortable ? onRemove : undefined}
                isSortable={isSortable}
                container={containerType}
                isEditMode={isEditMode}
              />
            ))}
          </SortableContext>
        </div>
      </div>
    </div>
  );
};
